import { getExternalApiUrl, useExternalApi, getSiteConfig } from './site-config';

// Dodajemy prosty mechanizm cachowania
const apiCache = {
  data: {},
  timestamp: {},
  ttl: 5 * 60 * 1000, // 5 minut w milisekundach

  // Sprawdza czy dane są w cache i czy są aktualne
  has(key) {
    const hasData = key in this.data;
    const isFresh = Date.now() - (this.timestamp[key] || 0) < this.ttl;
    return hasData && isFresh;
  },

  // Pobiera dane z cache
  get(key) {
    return this.data[key];
  },

  // Zapisuje dane do cache with eviction of stale entries
  set(key, data) {
    this.data[key] = data;
    this.timestamp[key] = Date.now();

    // Evict stale entries to prevent unbounded memory growth
    const now = Date.now();
    for (const k in this.timestamp) {
      if (now - this.timestamp[k] > this.ttl) {
        delete this.data[k];
        delete this.timestamp[k];
      }
    }

    return data;
  },

  // Czyści cache dla danego klucza
  clear(key) {
    if (key) {
      delete this.data[key];
      delete this.timestamp[key];
    } else {
      this.data = {};
      this.timestamp = {};
    }
  }
};

/**
 * Tworzy pełny URL API z obsługą dynamicznych domen
 * @param {string} path - Ścieżka względna do zasobu
 * @returns {string} - Pełny URL API
 */
export function getApiURL(path = '') {
  const config = getSiteConfig();

  // Jeśli używamy zewnętrznego API
  if (useExternalApi()) {
    // Sprawdź czy path to nazwa endpointu z mapowania
    const endpointName = path.replace('.php', '');

    // Mapowanie specjalnych nazw endpointów
    const endpointMapping = {
      'nadchodzaces': 'upcoming',
      'courses': 'courses'
    };

    // Spróbuj znaleźć mapowanie dla danego endpointu
    const mappedName = endpointMapping[endpointName] || endpointName;

    if (config.api.externalEndpoints[mappedName]) {
      return `${config.backend.url}${config.api.externalEndpoints[mappedName]}`;
    }

    // Fallback - buduj URL bezpośrednio
    const baseUrl = config.api.externalBaseUrl;
    if (!path) return baseUrl;

    // Sprawdź czy baseUrl już zawiera pełny URL (dla kompatybilności z dynamiczną konfiguracją)
    let finalBaseUrl = baseUrl;
    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      finalBaseUrl = `${config.backend.url}${baseUrl}`;
    }

    const cleanBaseUrl = finalBaseUrl.endsWith('/') ? finalBaseUrl.slice(0, -1) : finalBaseUrl;
    const endpoint = path.startsWith('/') ? path.slice(1) : path;
    return `${cleanBaseUrl}/${endpoint}`;
  }

  // Używamy wewnętrznego API
  const baseUrl = config.api.baseUrl;
  if (!path) return baseUrl;

  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const endpoint = path.startsWith('/') ? path.slice(1) : path;
  return `${cleanBaseUrl}/${endpoint}`;
}

/**
 * Pobiera dane z API
 * @param {string} endpoint - URL endpointu API
 * @param {object} options - Opcje dla zapytania API
 * @returns {Promise<any>} - Dane z API
 */
export async function fetchAPI(endpoint, options = {}) {
  const config = getSiteConfig();

  const mergedOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    mode: 'cors',
    cache: 'no-store',
    useCache: false,
    timeout: config.api.timeout,
    ...options,
  };

  // AbortController with timeout
  const controller = new AbortController();
  const timeoutMs = mergedOptions.timeout || 10000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  // Merge external signal if provided
  const signal = options.signal || controller.signal;

  // Mapowanie nazw endpointów dla Next.js API
  const endpointMapping = {
    'nadchodzaces': 'upcoming',
    'courses': 'courses'
  };

  // Formatowanie endpointu - dodaj .php tylko dla external API
  let apiEndpoint;
  if (useExternalApi()) {
    // Dla external API (PHP) dodaj .php jeśli nie ma
    apiEndpoint = endpoint.endsWith('.php') ? endpoint : `${endpoint}.php`;
  } else {
    // Dla wewnętrznego Next.js API użyj mapowania
    const mappedEndpoint = endpointMapping[endpoint] || endpoint;
    apiEndpoint = mappedEndpoint;
  }

  // Dodajemy parametry do URL, jeśli są potrzebne
  let endpointWithParams = apiEndpoint;
  
  // Dodaj znak ? jeśli mamy dodatkowe parametry w options.params
  if (options.params) {
    const queryParams = new URLSearchParams();
    
    // Dodaj wszystkie parametry z options.params
    for (const [key, value] of Object.entries(options.params)) {
      if (typeof value === 'object') {
        // Obsługa zagnieżdżonych parametrów (np. filters)
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          queryParams.append(`${key}[${nestedKey}]`, nestedValue);
        }
      } else {
        queryParams.append(key, value);
      }
    }
    
    const queryString = queryParams.toString();
    if (queryString) {
      endpointWithParams = `${apiEndpoint}?${queryString}`;
    }
  }
  
  // Poprawna konstrukcja adresu URL
  const requestUrl = getApiURL(endpointWithParams);
  const cacheKey = requestUrl;

  try {
    const fetchResponse = await fetch(requestUrl, {
      method: 'GET',
      headers: mergedOptions.headers,
      cache: 'no-store',
      mode: mergedOptions.mode,
      signal
    });

    clearTimeout(timeoutId);

    if (fetchResponse.ok) {
      const textResponse = await fetchResponse.text();

      try {
        const data = JSON.parse(textResponse);
        return data;
      } catch (jsonError) {
        throw new Error('Błąd parsowania JSON: ' + jsonError.message);
      }
    } else {
      throw new Error(`Błąd pobierania danych (status: ${fetchResponse.status})`);
    }
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * Pobiera listę kursów
 * @returns {Promise<Array>} - Lista kursów
 */
export async function getCourses() {
  try {
    const response = await fetchAPI('courses', {
      useCache: false // Nie używaj cache dla kursów
    });

    if (!response || !response.data) {
      return [];
    }

    // Dane przychodzą bezpośrednio z MySQL (płaska struktura)
    return response.data;
  } catch (error) {
    return [];
  }
}

/**
 * Pobiera szczegóły kursu po ID
 * @param {number|string} id - ID kursu
 * @returns {Promise<object|null>} - Dane kursu lub null jeśli nie znaleziono
 */
export async function getCourse(id) {
  try {
    if (!id) {
      return null;
    }
    
    const courseId = parseInt(id);
    
    // Bezpośrednie pobieranie kursu po ID
    const endpoint = 'courses';
    const apiResponse = await fetchAPI(endpoint, {
      params: { id: courseId },
      useCache: true // Używaj cache dla szczegółów kursu
    });
    
    if (apiResponse && apiResponse.data) {
      return apiResponse.data;
    }
    
    return null;
    
  } catch (error) {
    return null;
  }
}

/**
 * Pobiera listę nadchodzących kursów
 * @returns {Promise<Array>} - Lista nadchodzących kursów
 */
export async function getNadchodzace() {
  try {
    const response = await fetchAPI('nadchodzaces', {
      useCache: false // Nie używaj cache dla nadchodzących kursów
    });

    if (!response || !response.data) {
      return [];
    }

    // Dane już przychodzą we właściwym formacie z PHP
    return response.data;
  } catch (error) {
    return [];
  }
}

/**
 * Pobiera kurs po kodzie IMO
 * @param {string|number} kodimo - Kod IMO kursu
 * @returns {Promise<object|null>} - Dane kursu lub null jeśli nie znaleziono
 */
export async function getCourseByKodIMO(kodimo) {
  try {
    if (!kodimo) {
      return null;
    }

    const kodimoValue = String(kodimo).trim();
    
    // Pobieranie kursu po kodzie IMO poprzez filtr
    const apiResponse = await fetchAPI('courses', {
      params: {
        filters: { kodimo: kodimoValue }
      },
      useCache: true // Używaj cache dla kursów po kodzie IMO
    });
    
    if (apiResponse?.data && apiResponse.data.length > 0) {
          // Jeśli znaleziono kurs, użyj pierwszego dopasowania
      return apiResponse.data[0];
    }
    
    return null;
    
  } catch (error) {
    return null;
  }
}

/**
 * Pobiera kurs motorowodny
 * @param {string|number} motorowodneId - ID kursu motorowodnego
 * @returns {Promise<object|null>} - Dane kursu lub null jeśli nie znaleziono
 */
export async function getCourseByMotorBoat(motorowodneId) {
  try {
    if (!motorowodneId) {
      return null;
    }

    const motorowodneValue = parseInt(motorowodneId);
    
    // Pobieranie kursu motorowodnego poprzez filtr
    const apiResponse = await fetchAPI('courses', {
      params: {
        filters: { motorowodne: motorowodneValue }
      },
      useCache: true // Używaj cache dla kursów motorowodnych
    });
    
    if (apiResponse?.data && apiResponse.data.length > 0) {
          // Jeśli znaleziono kurs, użyj pierwszego dopasowania
      return apiResponse.data[0];
    }
    
    return null;
    
  } catch (error) {
    return null;
  }
}

/**
 * Pobiera szczegóły wydarzenia po ID
 * @param {number|string} id - ID wydarzenia
 * @returns {Promise<object|null>} - Dane wydarzenia lub null jeśli nie znaleziono
 */
export async function getEventById(id) {
  try {
    if (!id) {
      return null;
    }
    
    const eventId = parseInt(id);
    
    // Bezpośrednie pobieranie wydarzenia po ID
    const endpoint = 'nadchodzaces';
    const apiResponse = await fetchAPI(endpoint, {
      params: { id: eventId },
      useCache: true // Używaj cache dla szczegółów wydarzenia
    });
    
    if (apiResponse && apiResponse.data) {
      return apiResponse.data;
    }
    
    return null;
    
  } catch (error) {
    return null;
  }
}

/**
 * Pobiera kurs po idszkolenie
 * @param {string|number} idszkolenie - ID szkolenia
 * @returns {Promise<object|null>} - Dane kursu lub null jeśli nie znaleziono
 */
export async function getCourseByIdSzkolenie(idszkolenie) {
  try {
    if (!idszkolenie) {
      return null;
    }

    // Pobierz wszystkie kursy i znajdź ten z odpowiednim ID
    const courses = await getCourses();
    if (!courses || !Array.isArray(courses)) {
      return null;
    }
    
    const course = courses.find(c => c.id === parseInt(idszkolenie));
    return course || null;
    
  } catch (error) {
    return null;
  }
}

/**
 * Testuje połączenie z API
 */
export async function testApiConnection() {
  try {
    const config = getSiteConfig();
    
    // Funkcja pomocnicza do wykonania fetch z timeout
    const fetchWithTimeout = async (url, options = {}, timeout = config.api.timeout) => {
      const controller = new AbortController();
      const { signal } = controller;
      
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, timeout);
      
      try {
        const response = await fetch(url, { ...options, signal });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    };

    // Testujemy połączenie z API
    const testUrl = getApiURL('courses.php');
    
    const response = await fetchWithTimeout(testUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return {
      success: response.ok,
      status: response.status,
      statusText: response.statusText
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Czyści cache API
 * @param {string} key - Opcjonalny klucz do wyczyszczenia konkretnego elementu cache
 */
export function clearApiCache(key = null) {
  try {
    apiCache.clear(key);
  } catch (error) {
    // Ignoruj błędy
  }
} 